import { useMemo } from 'react';
import { Heading, useTheme } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import PageAnchor from '@components/PageAnchor';
import type { Entity as InsiderTradingEntity } from '../InsiderTrading';
import type {
  OwnershipEntity,
  OwnershipDataFormatted,
} from '../OwnershipChart';
import { LegacyAnchor } from '@/constants/anchors';
import {
  COMPANY_INSIDER_TRADING,
  COMPANY_OWNERSHIP_BREAKDOWN,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import type { InsiderTradingTransactionsProps } from '@/pages/CompanyReport/components/InsiderTradingTransactions';
import type {
  Shareholder,
  OwnerType,
} from '@/pages/CompanyReport/redux/interface/interface';
import ListTruncator from '@/components/ListTruncator';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import { SubHeadingWrapper, TotalOwnership } from './styled';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import useWindowSize from '@/hooks/useWindowSize';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import { useTranslation } from '@/hooks/useTranslation';
const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const TopShareholders = loadable(
  () => import('@/pages/CompanyReport/components/Ownership'),
  { resolveComponent: (component) => component.TopShareholders }
);
const OwnershipChart = loadable(() => import('../OwnershipChart'));
const OwnershipTable = loadable(() => import('../OwnershipTable'));

const InsiderTradingTransactions = loadable(
  () => import('@/pages/CompanyReport/components/InsiderTradingTransactions'),
  {
    resolveComponent: (component) => component.InsiderTradingTransactions,
  }
);
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

const InsiderTrading = loadable(() => import('../InsiderTrading'), {
  ssr: false,
});

interface ManagementStatements {
  isCEOCompensationAppropriate: SelectedStatement;
  isCEOCompensationChangeJustified: SelectedStatement;
  isManagementTeamSeasoned: SelectedStatement;
  isBoardSeasoned: SelectedStatement;
  hasNetInsiderBuyingOver3Months: SelectedStatement;
  isNotDilutedOverPastYear: SelectedStatement;
}
export interface ShareholderWithTitle extends Shareholder {
  holdingValue: string;
  sharesHeldFormat: string;
  owner: {
    name: string;
    type: OwnerType;
    companyType: number;
    objectId: number;
    teamTitle: string;
    boardTitle: string;
  };
}
export interface OwnershipProps {
  sectionNumber?: number;
  companyId: string;
  uniqueSymbol: string;
  statements: ManagementStatements;
  skipDefer: boolean;
  insiderTradingTransactions: InsiderTradingTransactionsProps[];
  topShareholders: ShareholderWithTitle[];
  totalOwnershipPercent: string;
  viewMode: ViewMode;
  /**
   * Ownership Chart
   */
  ownership: Partial<OwnershipEntity>[];
  totalOwnedShares: number;
  ownershipDataFormatted: OwnershipDataFormatted;
  /**
   * Insider Trading
   */
  insiderTrading: InsiderTradingEntity[];
}

const Ownership = ({
  sectionNumber,
  companyId,
  uniqueSymbol,
  statements: { hasNetInsiderBuyingOver3Months, isNotDilutedOverPastYear },
  skipDefer,
  insiderTradingTransactions,
  topShareholders,
  totalOwnershipPercent,
  viewMode,
  /**
   * Ownership Chart
   */
  ownership,
  ownershipDataFormatted,
  totalOwnedShares,
  /**
   * Insider Trading
   */
  insiderTrading,
}: OwnershipProps) => {
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const { insiderTradingDownloadUrl, ownershipSummaryDownloadUrl } =
    useMemo(() => {
      return {
        insiderTradingDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.InsiderTradingVolume
        ),
        ownershipSummaryDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.OwnershipBreakdown
        ),
      };
    }, [companyId]);
  const theme = useTheme();
  const { width: screenWidth } = useWindowSize();
  const headingHasOpacity = screenWidth && screenWidth <= theme.lgMinRaw;
  const t = useTranslation();
  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="ownership-section">
      <SectionSchema section="ownership" />
      <ReportSectionHeader
        title={
          <CompanySectionTitle
            title={t('page.companyReport.ownership.heading')}
          />
        }
        sectionDescription={t('page.companyReport.ownership.description')}
      />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.ownership"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>
      <PageAnchor
        type="sub-section-title"
        id={LegacyAnchor.OWNERSHIP_INSIDER_TRADING}
      />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={t('page.companyReport.ownership.subHeading1')}
        body={
          <>
            {insiderTradingTransactions.length > 0 && (
              <InsiderTradingTransactions
                data={insiderTradingTransactions}
                uniqueSymbol={uniqueSymbol}
                viewMode={viewMode}
              />
            )}
            <SubHeadingWrapper>
              {headingHasOpacity ? (
                <Heading
                  as="h4"
                  typography="baseStrong"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  {t(
                    'page.companyReport.ownership.insiderTradingVolume.heading'
                  )}
                </Heading>
              ) : (
                <Heading as="h4" typography="baseStrong" color="white">
                  {t(
                    'page.companyReport.ownership.insiderTradingVolume.heading'
                  )}
                </Heading>
              )}
            </SubHeadingWrapper>
            <DeferRenderUntilIntersecting
              preRenderHeight="466"
              preRenderHeightMdDown="562"
              skipDefer={skipDefer}
            >
              {() => (
                <>
                  <InsiderTrading data={insiderTrading} />
                  <ChartActionsContainer
                    companyName={uniqueSymbol}
                    feedbackLabel={COMPANY_INSIDER_TRADING}
                    exportDarkUrl={insiderTradingDownloadUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          </>
        }
        statements={
          <Statement
            title={hasNetInsiderBuyingOver3Months.title}
            description={
              <Description statement={hasNetInsiderBuyingOver3Months} />
            }
            value={hasNetInsiderBuyingOver3Months.value}
            statementId="hasNetInsiderBuyingOver3Months"
          />
        }
      />
      <hr />
      {ownership.length > 0 && (
        <>
          <PageAnchor type="sub-section-title" id={LegacyAnchor.OWNERSHIP} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('page.companyReport.ownership.subHeading2')}
            body={
              <ChartWrapper
                id="ownership-breakdown-chart"
                chart={
                  <OwnershipChart
                    data={ownership}
                    dataFormatted={ownershipDataFormatted}
                    totalOwnedShares={totalOwnedShares}
                  />
                }
                table={
                  <OwnershipTable
                    data={ownership}
                    dataFormatted={ownershipDataFormatted}
                  />
                }
                preRenderHeight={336}
                actionsMarginTop={16}
                exportDarkUrl={ownershipSummaryDownloadUrl}
                feedbackLabel={COMPANY_OWNERSHIP_BREAKDOWN}
              />
            }
            statements={
              <Statement
                title={isNotDilutedOverPastYear.title}
                description={
                  <Description statement={isNotDilutedOverPastYear} />
                }
                value={isNotDilutedOverPastYear.value}
                statementId="isNotDilutedOverPastYear"
              />
            }
          />
        </>
      )}
      <PageAnchor type="sub-section-title" id={LegacyAnchor.OWNERSHIP} />
      {topShareholders.length > 0 && (
        <>
          <hr />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('page.companyReport.ownership.subHeading3')}
            body={
              <>
                <TotalOwnership>
                  {`Top ${topShareholders.length} shareholders own ${totalOwnershipPercent}% of the company`}
                </TotalOwnership>
                {topShareholders.length > 10 ? (
                  <ListTruncator viewMode={viewMode} minHeight={360}>
                    <TopShareholders
                      topShareholders={topShareholders}
                      viewMode={viewMode}
                    />
                  </ListTruncator>
                ) : (
                  <TopShareholders
                    topShareholders={topShareholders}
                    viewMode={viewMode}
                  />
                )}
              </>
            }
          />
        </>
      )}
    </ReportSection>
  );
};

export default Ownership;
